"use client";

import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { Sidebar } from "./Sidebar";
import { MobileNav } from "./MobileNav";
import { MobileDrawer } from "./MobileDrawer";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import { useSettings, themes } from "@/contexts/SettingsContext";
import { MobileNavProvider } from "@/contexts/MobileNavContext";

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, isLoading } = useAuth();
  const { settings } = useSettings();
  const theme = themes[settings.theme];
  const isDesktop = useIsDesktop();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // 不需要认证的页面
  const publicPaths = ["/login"];
  const isPublicPage = publicPaths.includes(pathname);

  useEffect(() => {
    if (!isLoading && !user && !isPublicPage) {
      router.push("/login");
    }
  }, [isLoading, user, isPublicPage, router]);

  // 加载中
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center" style={{ backgroundColor: theme.bg }}>
        <div style={{ color: theme.textMuted }}>加载中...</div>
      </div>
    );
  }

  // 公开页面（如登录页）
  if (isPublicPage) {
    return <>{children}</>;
  }

  // 未登录，显示空白（等待重定向）
  if (!user) {
    return (
      <div className="flex h-full items-center justify-center" style={{ backgroundColor: theme.bg }}>
        <div style={{ color: theme.textMuted }}>正在跳转到登录页...</div>
      </div>
    );
  }

  // 已登录，显示带侧边栏的布局
  return (
    <MobileNavProvider>
      <div className={!isDesktop ? 'pwa-container' : 'flex h-screen'}>
        {/* 桌面端侧边栏 */}
        {isDesktop && <Sidebar />}

        {/* 移动端抽屉 */}
        {!isDesktop && (
          <MobileDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
          />
        )}

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* 移动端顶部导航 */}
          {!isDesktop && (
            <MobileNav onMenuClick={() => setIsDrawerOpen(true)} />
          )}

          <main className="flex-1 overflow-hidden">
            {children}
          </main>
        </div>
      </div>
    </MobileNavProvider>
  );
}
